import { useEffect, useMemo, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import {
  cancelWizardJob, completeSkeleton, createAppProject, createSkeleton, createWizardJob, deleteAppProject,
  generateCreateFeatureGraphic, generateCreateScreenshot, generateDescriptions, generateIcon,
  getActiveWizardJob, getLastWizardJob, getWizardJob, listAppProjects, listBrandPresets, listCreateStubs,
  listStubScreenshots, localizeWizardJob, planCreateScreenshots, shotBrandOf, shotBrandOfPlan, suggestBundle,
  updateAppProject, uploadCreateAsset, uploadCreateScreenshot,
  type AppProject, type BrandPack, type MetaTone, type CompleteSkeletonResult, type CreateSkeletonResult,
  type StubScreenshot, type WizardCopyResult, type WizardIconResult,
  type WizardBannerResult, type WizardJobDetail, type WizardPlan, type WizardScreenshotResult,
  type WizardShotSource,
} from '@/api/apps-setup';
import { VERTICAL_OPTIONS, type Vertical } from '@/lib/vertical';
import { ApiError } from '@/api/client';
import { ACCENT_PRESETS, renderBanner, renderPhoneScreenshot, type AccentPreset } from '@/lib/marketing-image';
import {
  assignRepoCaptions, buildLocalizePayload, buildWizardDraft, canCreateAppEarly, canUseWizardDraft,
  hasLocTasks, locProgressByLang, localizedViews, type AppWizardDraft, type LocalizeShotInput, type LocalizedView,
} from '@/lib/app-wizard';
import { getBuildConfig, startBuild, type StartedBuild } from '@/api/app-builds';
import { approveFactoryRun, createClientFactoryRun, createFactoryRun, nudgeFactoryRun } from '@/api/factory';
import { FactoryPublishOn, publishOnInput, type PublishOnValue } from './FactoryPublishOn';
import {
  ClientDomainChoice, FactoryDomainChoice, NO_DOMAIN_CHOICE, domainChoiceProblem, domainInput, type DomainChoiceValue,
} from './FactoryDomainChoice';
import { getPublisherOptions } from '@/api/publishers';
import { PLAY_LOCALES } from '@/lib/playLocales';
import { MarketPicker } from '@/pages/aso/MarketPicker';
import {
  AspectMisfit, prepareImageUpload, prepareScreenshotUpload, STORE_SCREENSHOT, WIZARD_FEATURE_BANNER, WIZARD_ICON,
} from '@/lib/image-upload';

export type { AppWizardDraft };

const EMPTY_PACK: BrandPack = {
  brand: '', vertical: null, keywords: '', brief: '', brand_bg: '', brand_style: '', phone_mockups: false,
  shot_orient: 'portrait', logo_key: '', meta_tone: 'aggressive',
};

/** The brand pack of a project / preset / job, without the rest of the row. */
function packOf(src: Omit<BrandPack, 'brief' | 'meta_tone'> & { brief?: string; meta_tone?: MetaTone }): BrandPack {
  return {
    brand: src.brand ?? '', vertical: src.vertical ?? null, keywords: src.keywords ?? '', brief: src.brief ?? '',
    brand_bg: src.brand_bg ?? '', brand_style: src.brand_style ?? '', phone_mockups: !!src.phone_mockups,
    shot_orient: src.shot_orient === 'landscape' ? 'landscape' : 'portrait', logo_key: src.logo_key ?? '',
    meta_tone: src.meta_tone === 'safe' ? 'safe' : 'aggressive',
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUseDraft: (draft: AppWizardDraft) => void;
  /** Early create needs an ASO owner when a technical admin runs the wizard; builds are technical-only. */
  isTechnical: boolean;
  ownerAdminId: string;
  owners: { value: string; label: string }[];
  onOwnerChange: (id: string) => void;
  /** The app was created early from the wizard screen (name, bundle, icon); the run keeps going. */
  onCreated: (result: CreateSkeletonResult) => void;
  /** The early-created app received the rest of the package; the wizard is done. */
  onFinished: (result: CompleteSkeletonResult) => void;
  /**
   * App Factory mode: a publisher is picked up front, the stub is required,
   * and the result screen ends in "Approve & build" — the run then makes the
   * Firebase project, domain, policy, builds and the handoff link by itself.
   * `jobId` + `runId` reopen a run's job for its review.
   * `client`: an outside client's own run — no publisher, prices or
   * publish-on choices; the stub is one marked for clients, AI screenshots
   * only, and a support email for the privacy policy is required.
   */
  factory?: {
    runId?: string;
    jobId?: string;
    client?: boolean;
    onRunStarted?: (runId: string) => void;
    onApproved?: (runId: string) => void;
  };
}

const MAX_SHOTS = 8;
const DEFAULT_SHOTS = 7;
/** Play takes no listing with fewer; the server refuses a client's approval below it. */
const MIN_CLIENT_SHOTS = 2;

/**
 * How the draft is filled in. 'ai' runs the background job that writes the copy
 * and draws every image; 'manual' opens the very same result screen with empty
 * cards, so the operator supplies their own icon, frames, banner and texts —
 * and can still call AI on a single card without paying for a whole run.
 */
type WizardMode = 'ai' | 'manual';

const EMPTY_COPY: WizardCopyResult = { name: '', short_description: '', full_description: '', bundle_id: '' };

function Sparkles({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 3l1.3 4.1L17 9l-3.7 1.9L12 15l-1.3-4.1L7 9l3.7-1.9L12 3zM5 14l.8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14zM19 13l.8 2.2L22 16l-2.2.8L19 19l-.8-2.2L16 16l2.2-.8L19 13z" />
    </svg>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs text-content-subtle">
      <span className="w-3.5 h-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      {label}
    </span>
  );
}

/** A file picker styled as a button — the native input is unusable as it ships. */
function UploadButton({ label, accept = 'image/*', multiple = false, disabled, onFiles }: {
  label: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: FileList | null) => void;
}) {
  return (
    <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface-muted ${disabled ? 'opacity-40' : 'cursor-pointer hover:border-border-strong text-content'}`}>
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 16V4m0 0L7 9m5-5l5 5M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
      </svg>
      {label}
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
      />
    </label>
  );
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))), 'image/png');
  });
}

function downloadCanvas(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) { resolve(); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so Safari reliably starts the download before the blob URL is freed.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      resolve();
    }, 'image/png');
  });
}

function safeSlug(s: string): string {
  return (s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'app').slice(0, 40);
}

const TERMINAL_JOB = ['done', 'failed', 'cancelled'];
const TERMINAL_TASK = ['done', 'failed'];

function isJobSettled(detail: WizardJobDetail): boolean {
  return TERMINAL_JOB.includes(detail.job.status)
    && detail.tasks.every((t) => TERMINAL_TASK.includes(t.status));
}

export function AppWizardModal({
  isOpen, onClose, onUseDraft, isTechnical, ownerAdminId, owners, onOwnerChange, onCreated, onFinished, factory,
}: Props) {
  const factoryMode = !!factory;
  const clientFactory = !!factory?.client;
  const queryClient = useQueryClient();

  // ---- Setup screen state ----
  const [mode, setMode] = useState<WizardMode>('ai');
  const [projectId, setProjectId] = useState('');
  const [stubId, setStubId] = useState('');
  const [shotSource, setShotSource] = useState<WizardShotSource>('ai');
  /** A client's App Factory run: AI drawn screenshots or its own uploads. */
  const [clientShots, setClientShots] = useState<'ai' | 'own'>('ai');
  const [shotCount, setShotCount] = useState(DEFAULT_SHOTS);
  const [brief, setBrief] = useState('');
  // Step 6: instructions for the texts, keywords for this run (null = the project's), untranslatable names.
  const [metaNotes, setMetaNotes] = useState('');
  const [runKeywords, setRunKeywords] = useState<string | null>(null);
  const [protectedTerms, setProtectedTerms] = useState('');
  const [creating, setCreating] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState('');

  // Project inline editor
  const [projectEditorOpen, setProjectEditorOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectPrompt, setProjectPrompt] = useState('');
  const [projectPack, setProjectPack] = useState<BrandPack>(EMPTY_PACK);
  const [marketOpen, setMarketOpen] = useState(false);
  const [projectLogoUrl, setProjectLogoUrl] = useState<string | null>(null);
  const [presetName, setPresetName] = useState('');
  // AI screenshots drawn as phone mockups around the stub's real frames.
  const [uiRefs, setUiRefs] = useState(false);
  const [projectSaving, setProjectSaving] = useState(false);

  // ---- Job state ----
  const [jobId, setJobId] = useState<string | null>(null);
  const [plan, setPlan] = useState<WizardPlan | null>(null);
  /** A manual run has no job at all — this is what opens the result screen. */
  const [manual, setManual] = useState(false);

  // Local edits layered over task results.
  const [copy, setCopy] = useState<WizardCopyResult | null>(null);
  const [iconCandidates, setIconCandidates] = useState<string[] | null>(null);
  const [chosenIcon, setChosenIcon] = useState('');
  const [iconPrompt, setIconPrompt] = useState('');
  // What to change after seeing the candidates — sent with the next icon request.
  const [iconFix, setIconFix] = useState('');
  // The locale the result screen shows: the source draft, or a finished localization.
  const [viewLang, setViewLang] = useState('');
  const [aiShots, setAiShots] = useState<Record<number, WizardScreenshotResult>>({});
  const [busy, setBusy] = useState<string | null>(null);

  // Frames the operator uploaded themselves — added to whatever the run produced.
  const [uploadedShots, setUploadedShots] = useState<{ key: string; url: string }[]>([]);

  // The feature graphic that will be saved with the app: uploaded by hand, or
  // rendered here from the first screenshot and then saved.
  const [feature, setFeature] = useState<{ key: string; url: string } | null>(null);
  // The AI banner's tagline / composition, kept for a redraw. `tagline`
  // undefined = let the AI write one; '' = the app name alone.
  const [aiBanner, setAiBanner] = useState<{ tagline?: string; scene: string }>({ scene: '' });
  // The run's banner is dropped into the card once; a removed one stays removed.
  const runBannerTaken = useRef(false);
  // Job frames the operator removed: a later poll must not bring them back.
  const droppedShots = useRef(new Set<number>());

  // Repo mode: selected real frames + captions + composed uploads.
  const [selectedRepo, setSelectedRepo] = useState<number[]>([]);
  const [repoCaptions, setRepoCaptions] = useState<Record<number, string>>({});
  const [composed, setComposed] = useState<{ id: number; key: string; url: string }[]>([]);
  const [composing, setComposing] = useState<{ done: number; total: number } | null>(null);
  const [presetIndex, setPresetIndex] = useState(0);
  const preset = ACCENT_PRESETS[presetIndex % ACCENT_PRESETS.length];

  const [headline, setHeadline] = useState('');

  // Manual AI screenshot run: same two stages as the Create tab (plan, then one
  // image per call), kept local because there is no job to hang tasks on.
  const [manualShotCount, setManualShotCount] = useState(3);
  const [drawing, setDrawing] = useState<{ done: number; total: number } | null>(null);
  const [manualStyle, setManualStyle] = useState('');
  const [manualSubject, setManualSubject] = useState('');

  // Localization run: picked target locales + request state.
  const [locLangs, setLocLangs] = useState<string[]>([]);
  // ---- Early create: the app made from this screen before the run is done ----
  const [createdAppId, setCreatedAppId] = useState('');
  const [buildStarted, setBuildStarted] = useState<StartedBuild | null>(null);
  const [buildError, setBuildError] = useState('');
  const [locBusy, setLocBusy] = useState(false);
  const [locError, setLocError] = useState('');

  // ---- App Factory ----
  const [factoryRunId, setFactoryRunId] = useState('');
  const [publisherId, setPublisherId] = useState('');
  const [publishUsd, setPublishUsd] = useState('');
  const [rentUsd, setRentUsd] = useState('');
  const [sendToPublisher, setSendToPublisher] = useState(true);
  const [publishOn, setPublishOn] = useState<PublishOnValue | null>(null);
  const [domainChoice, setDomainChoice] = useState<DomainChoiceValue>(NO_DOMAIN_CHOICE);
  // A client run: the support address its privacy policy shows.
  const [supportEmail, setSupportEmail] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setMode('ai');
    setProjectId(''); setStubId(''); setShotSource('ai'); setShotCount(DEFAULT_SHOTS); setBrief('');
    setMetaNotes(''); setRunKeywords(null); setProtectedTerms(''); setIconFix(''); setViewLang('');
    setCreating(false); setConflict(false); setError('');
    setProjectEditorOpen(false); setEditingProjectId(null); setProjectName(''); setProjectPrompt(''); setProjectSaving(false);
    setJobId(null); setPlan(null); setManual(false);
    setCopy(null); setIconCandidates(null); setChosenIcon(''); setIconPrompt(''); setAiShots({}); setBusy(null);
    setUploadedShots([]); setFeature(null); setAiBanner({ scene: '' }); runBannerTaken.current = false; droppedShots.current.clear();
    setSelectedRepo([]); setRepoCaptions({}); setComposed([]); setComposing(null);
    setPresetIndex(Math.floor(Math.random() * ACCENT_PRESETS.length));
    setHeadline('');
    setManualShotCount(3); setDrawing(null); setManualStyle(''); setManualSubject('');
    setLocLangs([]); setLocBusy(false); setLocError('');
    setFactoryRunId(factory?.runId ?? ''); setPublisherId(''); setPublishUsd(''); setRentUsd(''); setSendToPublisher(true); setPublishOn(null);
    setDomainChoice(NO_DOMAIN_CHOICE); setSupportEmail('');
    if (factory?.jobId) setJobId(factory.jobId);
    // Only a fresh open resets; the factory props are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const publishers = useQuery({ queryKey: ['publisher-options'], queryFn: getPublisherOptions, enabled: isOpen && factoryMode && !clientFactory });
  const pickPublisher = (id: string) => {
    setPublisherId(id);
    const p = publishers.data?.find((item) => item.id === id);
    if (p) { setPublishUsd(String(p.publish_cents / 100)); setRentUsd(String(p.rent_cents / 100)); }
  };
  const usdToCents = (v: string) => Math.round(Number(v || 0) * 100);

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['app-projects'], queryFn: listAppProjects, enabled: isOpen,
  });
  const presets = useQuery({ queryKey: ['app-brand-presets'], queryFn: listBrandPresets, enabled: isOpen });
  const selectedProject = useMemo(() => projects.find((item) => item.id === projectId) ?? null, [projects, projectId]);
  const stubs = useQuery({ queryKey: ['apps-setup', 'create-stubs'], queryFn: listCreateStubs, enabled: isOpen });
  const stub = (stubs.data ?? []).find((item) => String(item.id) === stubId) ?? null;
  const domainProblem = factoryMode && !clientFactory && stub
    ? domainChoiceProblem(stub.domain, domainChoice, { publishing: !!publishOn?.accountId })
    : null;
  // A client run on an own domain may leave the address out: one is made up on the domain.
  const clientBuysDomain = !!stub && (stub.domain === 'req' || (stub.domain === 'opt' && domainChoice.ownDomain));
  const supportEmailOk = supportEmail.trim()
    ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supportEmail.trim())
    : clientBuysDomain;
  // Why a client run cannot start yet, or null.
  const clientProblem = !clientFactory ? null
    : !stubId ? 'Pick a template'
      : !supportEmailOk ? 'Enter the support email for the privacy policy'
        : null;

  // Keywords typed for one project's run must not ride along to another project.
  useEffect(() => { setRunKeywords(null); }, [projectId]);

  // Real-frame source needs a stub; drop back to AI when the stub is cleared.
  useEffect(() => {
    if (!stubId && shotSource === 'repo') setShotSource('ai');
  }, [stubId, shotSource]);

  const jobQuery = useQuery({
    queryKey: ['wizard-job', jobId],
    queryFn: () => getWizardJob(jobId!),
    enabled: isOpen && !!jobId,
    refetchInterval: (query) => {
      const detail = query.state.data;
      return detail && isJobSettled(detail) ? false : 2500;
    },
  });
  const detail = jobQuery.data;
  const job = detail?.job;
  const copyTask = detail?.tasks.find((t) => t.kind === 'copy');
  const iconTask = detail?.tasks.find((t) => t.kind === 'icon');
  const shotTasks = useMemo(
    () => (detail?.tasks ?? []).filter((t) => t.kind === 'screenshot').sort((a, b) => a.position - b.position),
    [detail],
  );
  const bannerTask = detail?.tasks.find((t) => t.kind === 'banner');
  // A job that draws no frames: every screenshot is uploaded on review.
  const ownShots = !!job && job.shot_source === 'ai' && job.shot_count === 0;
  const started = !!jobId || manual;

  // Merge finished task results into the local editable state exactly once.
  // A job that already created its app reopens with what the app carries —
  // the operator's name, bundle, texts and icon — not the AI draft.
  useEffect(() => {
    if (!detail) return;
    const app = detail.app;
    const c = detail.tasks.find((t) => t.kind === 'copy');
    if (c?.status === 'done' && c.result) {
      setCopy((prev) => prev ?? (app
        ? { ...(c.result as WizardCopyResult), name: app.name, bundle_id: app.bundle_id,
          short_description: app.short_description, full_description: app.full_description }
        : (c.result as WizardCopyResult)));
    }
    const i = detail.tasks.find((t) => t.kind === 'icon');
    if (i?.status === 'done' && i.result) {
      const candidates = (i.result as WizardIconResult).candidates;
      const appIcon = app?.icon_url;
      setIconCandidates((prev) => prev ?? (appIcon && !candidates.includes(appIcon) ? [appIcon, ...candidates] : candidates));
    }
    if (app?.icon_url) setChosenIcon((prev) => prev || app.icon_url!);
    setAiShots((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const t of detail.tasks) {
        if (t.kind === 'screenshot' && t.status === 'done' && t.result && !next[t.position] && !droppedShots.current.has(t.position)) {
          next[t.position] = t.result as WizardScreenshotResult;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [detail]);

  // The run's AI banner becomes the saved banner — unless the operator already
  // put one there (an upload wins) or removed the run's one.
  useEffect(() => {
    if (runBannerTaken.current || bannerTask?.status !== 'done' || !bannerTask.result) return;
    runBannerTaken.current = true;
    const r = bannerTask.result as WizardBannerResult;
    setFeature((prev) => prev ?? { key: r.key, url: r.url });
    setAiBanner({ tagline: r.tagline, scene: r.scene });
  }, [bannerTask]);

  // A rejoined job remembers the app it already created.
  useEffect(() => {
    if (job?.app_id) setCreatedAppId(job.app_id);
  }, [job?.app_id]);

  // ...and the icon prompt the run was started with, so Draw with AI keeps it.
  useEffect(() => {
    if (job?.icon_prompt) setIconPrompt((prev) => prev || job.icon_prompt!);
  }, [job?.icon_prompt]);

  // Build readiness of the early-created app, polled while the operator
  // connects its keys on the Connect tab.
  const buildCfg = useQuery({
    queryKey: ['build-config', createdAppId],
    queryFn: () => getBuildConfig(createdAppId),
    enabled: isOpen && isTechnical && !!createdAppId,
    refetchInterval: 10_000,
  });
  const readiness = buildCfg.data?.readiness;

  // The stub whose frame library is offered: the one frozen on the job, or the
  // one picked on the setup screen when there is no job.
  const repoId = manual
    ? (stubId ? Number(stubId) : null)
    : (job?.shot_source === 'repo' ? job.base_repo_id : null);
  const repoShots = useQuery({
    queryKey: ['stub-screenshots', repoId],
    queryFn: () => listStubScreenshots(repoId!),
    enabled: isOpen && repoId != null,
  });

  // ---- Setup actions ----

  const openProjectEditor = (project?: AppProject) => {
    setEditingProjectId(project?.id || null);
    setProjectName(project?.name || '');
    setProjectPrompt(project?.prompt || '');
    setProjectPack(project ? packOf(project) : EMPTY_PACK);
    setProjectLogoUrl(project?.logo_url ?? null);
    setPresetName('');
    setProjectEditorOpen(true);
    setError('');
  };

  /** Fill the editor from a lab brand preset; every field stays editable. */
  const applyPreset = (name: string) => {
    setPresetName(name);
    const preset = (presets.data ?? []).find((item) => item.name === name);
    if (!preset) return;
    setProjectName(preset.name);
    setProjectPrompt(preset.prompt);
    setProjectPack(packOf(preset));
    setProjectLogoUrl(preset.logo_url);
  };

  /**
   * First-screen brand picker: a project already built on that brand is
   * selected as is; otherwise the editor opens as a new project filled from
   * the preset, so the operator never has to find the pack inside "+ New".
   */
  const pickBrand = (name: string) => {
    if (!name) return;
    const key = name.trim().toLowerCase();
    const existing = projects.find((item) => (item.brand || '').trim().toLowerCase() === key)
      ?? projects.find((item) => item.name.trim().toLowerCase() === key);
    if (existing) {
      setProjectId(existing.id);
      setProjectEditorOpen(false);
      return;
    }
    openProjectEditor();
    applyPreset(name);
  };

  /** A project saved without a brand pack: the artwork falls back to the generic dark look. */
  const projectLacksBrandVisual = !!selectedProject && !(selectedProject.brand_style || '').trim();

  const uploadProjectLogo = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setProjectSaving(true); setError('');
    try {
      const up = await uploadCreateAsset(file, 'logo');
      setProjectPack((prev) => ({ ...prev, logo_key: up.key }));
      setProjectLogoUrl(up.url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Logo upload failed');
    } finally {
      setProjectSaving(false);
    }
  };

  const saveProject = async () => {
    if (!projectName.trim() || !projectPrompt.trim()) {
      setError('Project name and prompt are required');
      return;
    }
    setProjectSaving(true); setError('');
    try {
      const input = { name: projectName.trim(), prompt: projectPrompt.trim(), ...projectPack };
      const saved = editingProjectId
        ? await updateAppProject(editingProjectId, input)
        : await createAppProject(input);
      await queryClient.invalidateQueries({ queryKey: ['app-projects'] });
      setProjectId(saved.id);
      setProjectEditorOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to save project');
    } finally {
      setProjectSaving(false);
    }
  };

  const removeProject = async () => {
    if (!editingProjectId) return;
    setProjectSaving(true); setError('');
    try {
      await deleteAppProject(editingProjectId);
      await queryClient.invalidateQueries({ queryKey: ['app-projects'] });
      if (projectId === editingProjectId) setProjectId('');
      setProjectEditorOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to delete project');
    } finally {
      setProjectSaving(false);
    }
  };

  const generate = async () => {
    if (!projectId) return;
    setCreating(true); setError(''); setConflict(false);
    try {
      const wizardInput = {
        project_id: projectId,
        base_repo_id: stubId ? Number(stubId) : undefined,
        shot_source: shotSource,
        shot_count: shotCount,
        brief: brief.trim() || undefined,
        ui_refs: shotSource === 'ai' && !!stubId && uiRefs,
        meta_notes: metaNotes.trim() || undefined,
        ...(runKeywords !== null ? { keywords: runKeywords.trim() } : {}),
        protected_terms: protectedTerms.trim() || undefined,
        icon_prompt: iconPrompt.trim() || undefined,
      };
      if (clientFactory) {
        // No repo screenshots: the stub’s frame library is ours.
        const out = await createClientFactoryRun({
          wizard: { ...wizardInput, shot_source: clientShots, ui_refs: false },
          ...(stub?.domain === 'opt' ? { own_domain: domainChoice.ownDomain } : {}),
          contact_email: supportEmail.trim() || undefined,
        });
        setFactoryRunId(out.run_id);
        factory?.onRunStarted?.(out.run_id);
        setPlan(out.plan);
        setJobId(out.job_id);
        return;
      }
      if (factoryMode) {
        const out = await createFactoryRun({
          publisher_id: publisherId, publish_cents: usdToCents(publishUsd), rent_cents: usdToCents(rentUsd),
          send_to_publisher: sendToPublisher, wizard: wizardInput, ...publishOnInput(publishOn),
          ...domainInput(stub?.domain ?? null, domainChoice),
        });
        setFactoryRunId(out.run_id);
        factory?.onRunStarted?.(out.run_id);
        setPlan(out.plan);
        setJobId(out.job_id);
        return;
      }
      const out = await createWizardJob(wizardInput);
      setPlan(out.plan);
      setJobId(out.job_id);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        setConflict(true);
        setError('A wizard job is already running.');
      } else {
        setError(cause instanceof Error ? cause.message : 'Failed to start the job');
      }
    } finally {
      setCreating(false);
    }
  };

  /** Manual run: nothing is requested, the result screen just opens empty. */
  const startManual = () => {
    setError('');
    setCopy(EMPTY_COPY);
    setIconCandidates([]);
    setManual(true);
  };

  const openActive = async () => {
    setError('');
    try {
      const { job_id } = await getActiveWizardJob();
      if (!job_id) { setError('No active job found — try again.'); setConflict(false); return; }
      setPlan(null);
      setJobId(job_id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to look up the active job');
    }
  };

  // The last run, finished or not — the way back after a trip to the Connect
  // or Build tab (which unmounts this screen) to finish an early-created app.
  const openLast = async () => {
    setError('');
    try {
      const { job_id } = await getLastWizardJob();
      if (!job_id) { setError('You have no wizard jobs yet.'); return; }
      setPlan(null);
      setJobId(job_id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to look up the last job');
    }
  };

  // ---- Result actions ----

  // The brand pack every re-roll is made with: frozen on the job when there is
  // one, otherwise the selected project's — so a manual run and a background
  // run of the same project produce copy and art in the same identity.
  const pack: BrandPack = job ? packOf(job) : selectedProject ? packOf(selectedProject) : EMPTY_PACK;
  const shotBrand = plan ? shotBrandOfPlan(plan) : shotBrandOf(pack);
  const jobRefCount = job?.ui_ref_count ?? plan?.uiRefCount ?? 0;

  const rerollCopy = async () => {
    if (!copy?.name.trim()) { setError('Type the app name first — the copy is written from it.'); return; }
    setBusy('copy'); setError('');
    try {
      const out = await generateDescriptions({
        name: copy.name, brand: pack.brand || undefined, vertical: pack.vertical, meta_tone: pack.meta_tone,
        // A job froze its keywords; a manual draft uses the step-6 field when it was edited.
        keywords: (job ? pack.keywords : (runKeywords ?? pack.keywords)) || undefined,
        notes: (job ? job.meta_notes : metaNotes)?.trim() || undefined,
        protected_terms: (job ? job.protected_terms : protectedTerms)?.trim() || undefined,
      });
      setCopy((prev) => prev && { ...prev, short_description: out.short_description, full_description: out.full_description, gnl: out.gnl });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Copy generation failed');
    } finally {
      setBusy(null);
    }
  };

  const rerollBundle = async () => {
    if (!copy?.name.trim()) { setError('Type the app name first — the bundle id is suggested from it.'); return; }
    setBusy('bundle'); setError('');
    try {
      const out = await suggestBundle({
        name: copy.name, short_description: copy.short_description, full_description: copy.full_description,
        brand: pack.brand || undefined,
      });
      setCopy((prev) => prev && { ...prev, bundle_id: out.bundle_id });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Bundle suggestion failed');
    } finally {
      setBusy(null);
    }
  };

  /**
   * A new icon set. With a correction note and a picked icon (`editPicked`)
   * that icon is edited — the cheap way to fix one detail; otherwise the set
   * is redrawn from the brief with the note on top.
   */
  const rerollIcon = async (editPicked = false) => {
    // The run's brief (job) or the one typed on the setup screen; the project's default backs both.
    const runBrief = (job ? job.brief : brief)?.trim() || pack.brief;
    const ownPrompt = iconPrompt.trim();
    const prompt = ownPrompt || [runBrief, `the mobile game "${copy?.name || job?.subject || 'mobile game'}"`].filter(Boolean).join('\n');
    const correction = iconFix.trim();
    setBusy(editPicked ? 'icon-fix' : 'icon'); setError('');
    try {
      const out = await generateIcon({
        prompt, brand: pack.brand || copy?.name || undefined, brand_bg: pack.brand_bg || undefined, brand_style: pack.brand_style || undefined,
        // An icon prompt of its own decides the motif; the stub's game would pull it back to that game.
        stub_id: stubId && !ownPrompt ? Number(stubId) : undefined,
        logo_key: pack.logo_key || undefined,
        ...(correction ? { correction } : {}),
        ...(editPicked && correction && chosenIcon ? { base_icon_url: chosenIcon } : {}),
      });
      // Uploaded candidates stay: a generated set must not throw away a file
      // the operator picked by hand.
      setIconCandidates((prev) => [...out.candidates, ...(prev ?? []).filter((u) => u === chosenIcon)]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Icon generation failed');
    } finally {
      setBusy(null);
    }
  };

  const uploadIcons = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy('icon-upload'); setError('');
    const added: string[] = [];
    let failures = 0;
    for (const file of Array.from(files).slice(0, 4)) {
      try {
        const fitted = await prepareImageUpload(file, WIZARD_ICON);
        added.push((await uploadCreateAsset(fitted, 'icon')).url);
      } catch {
        failures++;
      }
    }
    setIconCandidates((prev) => [...added, ...(prev ?? [])]);
    if (added[0]) setChosenIcon(added[0]);
    setBusy(null);
    if (failures > 0) setError(`${failures} icon file(s) could not be read — use PNG or JPEG.`);
  };

  const uploadShots = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy('shot-upload'); setError('');
    const added: { key: string; url: string }[] = [];
    let failures = 0;
    let misfit = '';
    for (const file of Array.from(files).slice(0, MAX_SHOTS)) {
      try {
        const fitted = await prepareScreenshotUpload(file, preset);
        added.push(await uploadCreateAsset(fitted, 'screenshot'));
      } catch (cause) {
        failures++;
        // A banner among the frames gets its own message: it is readable, just in the wrong slot.
        if (cause instanceof AspectMisfit) misfit = cause.message;
      }
    }
    setUploadedShots((prev) => [...prev, ...added]);
    setBusy(null);
    if (misfit) setError(`${misfit} Upload it on the Feature banner card below.`);
    else if (failures > 0) setError(`${failures} frame(s) could not be read — use PNG or JPEG.`);
  };

  const redoAiShot = async (position: number) => {
    const shot = aiShots[position];
    if (!shot) return;
    setBusy(`shot-${position}`); setError('');
    try {
      const out = await generateCreateScreenshot({
        style: job?.style ?? plan?.style ?? manualStyle,
        subject: job?.subject ?? plan?.subject ?? manualSubject,
        scene: shot.scene,
        caption: shot.caption,
        index: position,
        total: job?.shot_count ?? Object.keys(aiShots).length,
        ...shotBrand,
        ref_key: shot.ref_key,
        ui_ref_count: jobRefCount,
      });
      setAiShots((prev) => ({ ...prev, [position]: { ...prev[position], key: out.key, url: out.url } }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Screenshot generation failed');
    } finally {
      setBusy(null);
    }
  };

  /** Manual mode: plan a set from the copy typed so far, then draw it frame by frame. */
  const drawManualShots = async () => {
    setError(''); setDrawing({ done: 0, total: manualShotCount });
    try {
      const useRefs = uiRefs && !!stubId;
      // Real frames as UI references, in library order — one per scene.
      const refKeys = useRefs ? (await listStubScreenshots(Number(stubId))).map((f) => f.key) : [];
      const out = await planCreateScreenshots({
        name: copy?.name, short_description: copy?.short_description, full_description: copy?.full_description,
        brief: brief.trim() || pack.brief || undefined, stub_id: stubId ? Number(stubId) : undefined, count: manualShotCount,
        keywords: pack.keywords || undefined,
        ui_refs: useRefs && refKeys.length > 0,
        ...shotBrandOf(pack),
      });
      setManualStyle(out.style); setManualSubject(out.subject);
      const base = Object.keys(aiShots).length;
      let failures = 0;
      for (let i = 0; i < out.scenes.length; i++) {
        const scene = out.scenes[i];
        const refKey = i < out.uiRefCount ? refKeys[i] : undefined;
        try {
          const drawn = await generateCreateScreenshot({
            style: out.style, subject: out.subject, scene: scene.scene, caption: scene.caption,
            index: i, total: out.scenes.length, ...shotBrandOfPlan(out),
            ref_key: refKey, ui_ref_count: out.uiRefCount,
          });
          setAiShots((prev) => ({
            ...prev,
            [base + i]: { key: drawn.key, url: drawn.url, scene: scene.scene, caption: scene.caption, ref_key: refKey },
          }));
        } catch {
          failures++;
        }
        setDrawing({ done: i + 1, total: out.scenes.length });
      }
      if (failures > 0) setError(`${failures} frame(s) failed — redo just those.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Screenshot generation failed');
    } finally {
      setDrawing(null);
    }
  };

  /** The feature graphic drawn by AI in the run's style; replaces the saved banner. */
  const drawAiBanner = async () => {
    if (!copy?.name.trim()) { setError('Type the app name first — the banner draws it.'); return; }
    setBusy('banner'); setError('');
    try {
      const out = await generateCreateFeatureGraphic({
        name: copy.name, short_description: copy.short_description, full_description: copy.full_description,
        brief: (job ? job.brief : brief.trim() || pack.brief) || undefined,
        stub_id: job?.base_repo_id ?? (stubId ? Number(stubId) : undefined),
        keywords: pack.keywords || undefined,
        style: job?.style ?? plan?.style ?? (manualStyle || undefined),
        subject: job?.subject ?? plan?.subject ?? (manualSubject || undefined),
        ...(aiBanner.tagline !== undefined ? { tagline: aiBanner.tagline } : {}),
        ...(aiBanner.scene.trim() ? { scene: aiBanner.scene } : {}),
        ...shotBrand,
      });
      runBannerTaken.current = true;
      setFeature({ key: out.key, url: out.url });
      setAiBanner({ tagline: out.tagline, scene: out.scene });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Banner generation failed');
    } finally {
      setBusy(null);
    }
  };

  const dropAiShot = (position: number) => {
    droppedShots.current.add(position);
    setAiShots((prev) => {
      const next = { ...prev };
      delete next[position];
      return next;
    });
  };

  const toggleRepoFrame = (id: number) => {
    setSelectedRepo((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      setRepoCaptions((captions) => assignRepoCaptions(plan?.scenes ?? [], next, captions));
      return next;
    });
  };

  const compose = async () => {
    const frames = repoShots.data ?? [];
    const targets = selectedRepo
      .map((id) => frames.find((f) => f.id === id))
      .filter((f): f is NonNullable<typeof f> => !!f);
    if (targets.length === 0) return;
    setComposing({ done: 0, total: targets.length }); setError('');
    const out: { id: number; key: string; url: string }[] = [];
    let failures = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        const img = await loadImage(targets[i].url);
        const canvas = renderPhoneScreenshot(img, { preset, caption: repoCaptions[targets[i].id] ?? '' });
        const uploaded = await uploadCreateScreenshot(
          await prepareImageUpload(await canvasToBlob(canvas), STORE_SCREENSHOT),
        );
        out.push({ id: targets[i].id, ...uploaded });
      } catch {
        failures++;
      }
      setComposing({ done: i + 1, total: targets.length });
    }
    setComposed(out);
    setComposing(null);
    if (failures > 0) setError(`${failures} frame(s) failed to compose — try again.`);
  };

  const cancelJob = async () => {
    if (!jobId) return;
    setBusy('cancel'); setError('');
    try {
      await cancelWizardJob(jobId);
      await jobQuery.refetch();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to cancel the job');
    } finally {
      setBusy(null);
    }
  };

  // ---- Draft assembly ----

  const orderedAiShots = useMemo(
    () => Object.keys(aiShots).map(Number).sort((a, b) => a - b).map((p) => aiShots[p]),
    [aiShots],
  );
  // A manual run may mix all three sources; a job run ships what the job made,
  // plus anything the operator uploaded on top.
  const runShots: { key: string; url: string }[] = manual
    ? [...composed, ...orderedAiShots]
    : (job?.shot_source === 'repo' ? composed : orderedAiShots);
  const screenshotKeys = [...runShots.map((s) => s.key), ...uploadedShots.map((s) => s.key)];
  const tooFewShots = clientFactory && screenshotKeys.length < MIN_CLIENT_SHOTS;
  // Frames picked from the stub but not composed yet still belong in the
  // preview — they are what those slots are going to look like.
  const pendingRepoUrls = composed.length === 0
    ? selectedRepo.map((id) => repoShots.data?.find((f) => f.id === id)?.url).filter((u): u is string => !!u)
    : [];
  const previewShotUrls = [
    ...runShots.map((s) => s.url),
    ...pendingRepoUrls,
    ...uploadedShots.map((s) => s.url),
  ];
  const bannerSource = runShots[0]?.url ?? uploadedShots[0]?.url;

  const draftParts = {
    copy,
    iconUrl: chosenIcon,
    screenshotKeys,
    featureKey: feature?.key ?? '',
    stubId: job?.base_repo_id != null ? String(job.base_repo_id) : stubId,
    jobId: jobId ?? '',
  };
  const draftReady = canUseWizardDraft(draftParts);

  // ---- Localization run ----

  // The frames the localize request re-renders, in display order and with the
  // captions as they stand right now.
  const localizeShots: LocalizeShotInput[] = job?.shot_source === 'repo'
    ? composed.map((c) => ({ key: c.key, url: c.url, caption: repoCaptions[c.id] ?? '' }))
    : orderedAiShots.map((s) => ({ key: s.key, url: s.url, caption: s.caption }));
  const locStarted = hasLocTasks(detail?.tasks ?? []);
  const locViews = localizedViews(detail?.tasks ?? []);
  const viewLoc = locViews.find((v) => v.lang === viewLang) ?? null;
  const locProgress = locProgressByLang(detail?.tasks ?? [], locLangs);
  const canLocalize = job?.status === 'done' && !!copy && !locStarted;

  const startLocalization = async () => {
    if (!jobId || !copy || locLangs.length === 0) return;
    setLocBusy(true); setLocError('');
    try {
      await localizeWizardJob(jobId, buildLocalizePayload(locLangs, copy, localizeShots));
      await jobQuery.refetch(); // status flips back to running — polling resumes
    } catch (cause) {
      setLocError(cause instanceof Error ? cause.message : 'Failed to start localization');
    } finally {
      setLocBusy(false);
    }
  };

  const bannerSaver = useRef<(() => Promise<{ key: string; url: string }>) | null>(null);
  const [usingDraft, setUsingDraft] = useState(false);
  /** The draft parts with the rendered banner saved, when the operator did not save it. */
  const partsWithBanner = async () => {
    if (draftParts.featureKey || !bannerSaver.current) return draftParts;
    const saved = await bannerSaver.current();
    return { ...draftParts, featureKey: saved.key };
  };
  const useDraft = async () => {
    setUsingDraft(true); setError('');
    try {
      const draft = buildWizardDraft(await partsWithBanner());
      if (draft) onUseDraft(draft);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save the banner');
    } finally {
      setUsingDraft(false);
    }
  };

  // Early create: the build needs only the name, the bundle and the icon, so
  // the app is created now and the keys / build can be done on the other tabs
  // while the frames, texts and localizations keep generating. The bundle is
  // frozen from here on; everything else lands with Finish app.
  const canCreateEarly = canCreateAppEarly(draftParts) && !createdAppId && (!isTechnical || !!ownerAdminId);
  const createEarly = async (): Promise<string | null> => {
    if (!copy) return null;
    setBusy('create'); setError('');
    try {
      const r = await createSkeleton({
        name: copy.name, short_description: copy.short_description, full_description: copy.full_description,
        bundle_id: copy.bundle_id, icon_url: chosenIcon, platform: 'android',
        owner_admin_id: isTechnical ? ownerAdminId : undefined,
        // A client run's stub and keystore are attached by the server from the run.
        base_repo_id: !clientFactory && draftParts.stubId ? Number(draftParts.stubId) : undefined,
        wizard_job_id: jobId ?? undefined,
      });
      setCreatedAppId(r.app_id);
      // The server may have de-duplicated the bundle; the app carries the final one.
      setCopy((prev) => (prev ? { ...prev, bundle_id: r.bundle_id } : prev));
      onCreated(r);
      if (jobId) void queryClient.invalidateQueries({ queryKey: ['wizard-job', jobId] });
      // The run starts the Firebase project and the domain right away.
      if (factoryRunId) void nudgeFactoryRun(factoryRunId).catch(() => {});
      return r.app_id;
    } catch (cause) {
      // The app may be there anyway: a slow create can finish on the server
      // after the browser gave up on it, and a retry then answers 409. The job
      // knows the app it made — carry on with that one.
      const adopted = await adoptJobApp();
      if (adopted) return adopted;
      setError(cause instanceof Error ? cause.message : 'Failed to create the app');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const adoptJobApp = async (): Promise<string | null> => {
    if (!jobId) return null;
    try {
      const d = await getWizardJob(jobId);
      if (!d.job.app_id) return null;
      setCreatedAppId(d.job.app_id);
      if (d.app) setCopy((prev) => (prev ? { ...prev, bundle_id: d.app!.bundle_id } : prev));
      void queryClient.invalidateQueries({ queryKey: ['wizard-job', jobId] });
      if (factoryRunId) void nudgeFactoryRun(factoryRunId).catch(() => {});
      return d.job.app_id;
    } catch {
      return null;
    }
  };

  // App Factory: create (when not yet), save the reviewed package, hand the
  // rest to the run.
  const approveAndBuild = async () => {
    if (!copy || !factoryRunId) return;
    const appId = createdAppId || await createEarly();
    if (!appId) return;
    setUsingDraft(true); setError('');
    try {
      const parts = await partsWithBanner();
      const r = await completeSkeleton(appId, {
        name: copy.name, short_description: copy.short_description, full_description: copy.full_description,
        icon_url: chosenIcon || undefined,
        screenshot_keys: parts.screenshotKeys, feature_graphic_key: parts.featureKey || undefined,
        wizard_job_id: jobId ?? undefined,
      });
      await approveFactoryRun(factoryRunId);
      onFinished(r);
      factory?.onApproved?.(factoryRunId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to approve the package');
    } finally {
      setUsingDraft(false);
    }
  };

  const finishApp = async () => {
    if (!copy || !createdAppId) return;
    setUsingDraft(true); setError('');
    try {
      const parts = await partsWithBanner();
      const r = await completeSkeleton(createdAppId, {
        name: copy.name, short_description: copy.short_description, full_description: copy.full_description,
        icon_url: chosenIcon || undefined,
        screenshot_keys: parts.screenshotKeys, feature_graphic_key: parts.featureKey || undefined,
        wizard_job_id: jobId ?? undefined,
      });
      onFinished(r);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to finish the app');
    } finally {
      setUsingDraft(false);
    }
  };

  const build = async (type: 'apk' | 'aab') => {
    if (!createdAppId) return;
    setBusy('build'); setBuildError('');
    try {
      setBuildStarted(await startBuild(createdAppId, type));
      void queryClient.invalidateQueries({ queryKey: ['builds'] });
    } catch (cause) {
      setBuildError(cause instanceof Error ? cause.message : 'Failed to start the build');
    } finally {
      setBusy(null);
    }
  };

  const rerollButton = (key: string, label: string, onClick: () => void, disabled = false) => (
    <button
      type="button"
      disabled={Boolean(busy) || disabled}
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-hover disabled:opacity-40"
    >
      <Sparkles className={`w-3.5 h-3.5 ${busy === key ? 'animate-pulse' : ''}`} />
      {busy === key ? 'Generating…' : label}
    </button>
  );

  const inputClass = 'px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={factoryMode ? 'App Factory' : 'App Wizard'} size="full">
      {!started ? (
        <div className="min-h-full flex items-center justify-center py-6">
          <div className="w-full max-w-4xl">
            <div className="text-center mb-6">
              <div className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="w-6 h-6" /></div>
              <h3 className="text-2xl font-semibold text-content">
                {clientFactory ? 'New app' : factoryMode ? 'New app for a publisher' : 'Build a ready-to-create app'}
              </h3>
              <p className="mt-2 text-sm text-content-subtle">
                {clientFactory
                  ? 'AI writes the store package; you check it once and approve it. The app services, the domain, the privacy policy and the build follow by themselves.'
                  : factoryMode
                  ? 'AI writes the package; you check it once. Firebase, the domain, the privacy policy, the builds and the handoff link follow by themselves.'
                  : 'Let AI produce the whole draft from a project brief, or fill the same cards in by hand — your own icon, frames, banner and texts, with AI available on any single card.'}
              </p>
            </div>

            {clientFactory && (
              <div className="grid sm:grid-cols-2 gap-4 p-5 mb-4 bg-surface-muted/50 border border-border rounded-2xl">
                <div>
                  <label className="block text-xs font-semibold text-content-muted mb-2">Support email</label>
                  <input
                    type="email"
                    value={supportEmail}
                    onChange={(e) => setSupportEmail(e.target.value)}
                    placeholder={clientBuysDomain ? 'Leave empty to use one on the domain' : 'support@yourcompany.com'}
                    className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface text-content"
                  />
                  <p className="mt-1 text-[11px] text-content-subtle">
                    {clientBuysDomain
                      ? 'Shown in the app’s privacy policy as the contact address. Leave it empty and one like support@<the domain> is used — no mailbox receives its mail.'
                      : 'Required — shown in the app’s privacy policy as the contact address.'}
                  </p>
                </div>
                {stub ? (
                  <ClientDomainChoice
                    className="self-center"
                    rule={stub.domain}
                    ownDomain={domainChoice.ownDomain}
                    onChange={(ownDomain) => setDomainChoice({ ...domainChoice, ownDomain })}
                  />
                ) : (
                  <p className="self-center text-xs text-content-subtle">Own domain: decided by the template once one is picked below.</p>
                )}
              </div>
            )}

            {factoryMode && !clientFactory && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 p-5 mb-4 bg-surface-muted/50 border border-border rounded-2xl">
                <div>
                  <label className="block text-xs font-semibold text-content-muted mb-2">Publisher</label>
                  <Select
                    value={publisherId}
                    onChange={pickPublisher}
                    searchable
                    placeholder={publishers.isLoading ? 'Loading…' : 'Who will publish it'}
                    options={(publishers.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-content-muted mb-2">Publication, $</label>
                  <input type="number" min={0} step="0.01" value={publishUsd} onChange={(e) => setPublishUsd(e.target.value)} className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface text-content" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-content-muted mb-2">Rent per week, $</label>
                  <input type="number" min={0} step="0.01" value={rentUsd} onChange={(e) => setRentUsd(e.target.value)} className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface text-content" />
                </div>
                <label className="flex items-start gap-2 text-xs text-content-subtle self-end pb-2">
                  <input type="checkbox" className="mt-0.5" checked={sendToPublisher} onChange={(e) => setSendToPublisher(e.target.checked)} />
                  <span>
                    Send the link to the publisher's chat when it is ready
                    {publisherId && publishers.data?.find((p) => p.id === publisherId)?.has_chat === false ? ' (this publisher has no chat)' : ''}
                  </span>
                </label>
                <FactoryPublishOn
                  className="sm:col-span-2 lg:col-span-4"
                  value={publishOn}
                  onChange={(v) => {
                    // Publishing ourselves, the publisher needs no link in their chat.
                    if (v && !publishOn) setSendToPublisher(false);
                    setPublishOn(v);
                  }}
                />
                {stub ? (
                  <FactoryDomainChoice
                    className="sm:col-span-2 lg:col-span-4"
                    rule={stub.domain}
                    typeName={stub.type_name}
                    value={domainChoice}
                    onChange={setDomainChoice}
                    publishing={!!publishOn?.accountId}
                  />
                ) : (
                  <p className="sm:col-span-2 lg:col-span-4 text-xs text-content-subtle">
                    Own domain: decided by the stub’s repo type once a stub is picked below.
                  </p>
                )}
              </div>
            )}

            {!factoryMode && (
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              {([
                ['ai', 'Generate everything', 'Copy, icon candidates and screenshots are produced in one background run.'],
                ['manual', 'Fill in manually', 'Empty cards: upload your own icon, screenshots and feature banner, type the texts.'],
              ] as const).map(([value, title, hint]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setMode(value); setError(''); setConflict(false); }}
                  className={`text-left p-4 rounded-2xl border transition-colors ${mode === value ? 'border-primary bg-primary/5' : 'border-border hover:border-border-strong'}`}
                >
                  <p className={`text-sm font-semibold ${mode === value ? 'text-primary' : 'text-content'}`}>{title}</p>
                  <p className="mt-1 text-xs text-content-subtle">{hint}</p>
                </button>
              ))}
            </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 p-5 bg-surface-muted/50 border border-border rounded-2xl">
              {mode === 'ai' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-content-muted">1. App project</label>
                    <div className="flex items-center gap-2">
                      {projectId && <button type="button" onClick={() => openProjectEditor(projects.find((item) => item.id === projectId))} className="text-[11px] font-medium text-content-subtle hover:text-primary">Edit</button>}
                      <button type="button" onClick={() => openProjectEditor()} className="text-[11px] font-medium text-primary hover:text-primary-hover">+ New</button>
                    </div>
                  </div>
                  <Select value={projectId} onChange={setProjectId} searchable placeholder={projectsLoading ? 'Loading…' : 'Select project'} options={projects.map((item) => ({ value: item.id, label: item.name }))} />
                  <div className="mt-2">
                    <Select
                      value=""
                      onChange={pickBrand}
                      searchable
                      placeholder={presets.isLoading ? 'Loading brands…' : 'Or start from a brand pack…'}
                      options={(presets.data ?? []).map((item) => ({ value: item.name, label: `${item.name} · ${item.vertical ?? ''}` }))}
                    />
                  </div>
                  {projectLacksBrandVisual && (
                    <p className="mt-1.5 text-[11px] text-warning">
                      No brand visual on this project — icon and screenshots fall back to a generic dark look. Edit → fill from a brand pack.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-content-muted mb-2">
                  {mode === 'ai' ? '2. ' : ''}{clientFactory ? 'Template' : 'Stub (base repo)'}
                </label>
                <Select
                  value={stubId}
                  onChange={setStubId}
                  placeholder={stubs.data && stubs.data.length === 0
                    ? (clientFactory ? '— no templates yet —' : '— no stubs —')
                    : (clientFactory ? 'Select template' : 'Select stub')}
                  options={[
                    ...(clientFactory ? [] : [{ value: '', label: 'No stub' }]),
                    ...(stubs.data ?? []).map((s) => ({ value: String(s.id), label: s.notes ? `${s.name} · ${s.notes}` : s.name })),
                  ]}
                />
                {mode === 'manual' && (
                  <p className="mt-1 text-[11px] text-content-subtle">Optional — it seeds the build config and opens the stub's real frame library.</p>
                )}
                {factoryMode && !stubId && (
                  <p className="mt-1 text-[11px] text-content-subtle">
                    {clientFactory && stubs.data && stubs.data.length === 0
                      ? 'No template is open to you yet — ask us to enable one.'
                      : 'Required — the build is made from it.'}
                  </p>
                )}
              </div>
              {mode === 'ai' && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-2">3. Screenshots</label>
                    {clientFactory ? (
                      <div className="grid grid-cols-2 p-1 rounded-lg border border-border bg-surface">
                        {(['ai', 'own'] as const).map((item) => (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setClientShots(item)}
                            className={`py-1.5 rounded-md text-sm font-medium transition-colors ${clientShots === item ? 'bg-primary text-white shadow-sm' : 'text-content-subtle hover:text-content'}`}
                          >
                            {item === 'ai' ? 'AI generated' : 'My own'}
                          </button>
                        ))}
                      </div>
                    ) : (
                    <>
                    <div className="grid grid-cols-2 p-1 rounded-lg border border-border bg-surface">
                      {(['ai', 'repo'] as const).map((item) => (
                        <button
                          key={item}
                          type="button"
                          disabled={item === 'repo' && !stubId}
                          title={item === 'repo' && !stubId ? 'Pick a stub first' : undefined}
                          onClick={() => setShotSource(item)}
                          className={`py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-40 ${shotSource === item ? 'bg-primary text-white shadow-sm' : 'text-content-subtle hover:text-content'}`}
                        >
                          {item === 'ai' ? 'AI generated' : 'Real game frames'}
                        </button>
                      ))}
                    </div>
                    {shotSource === 'ai' && stubId && (
                      <label className="mt-2 flex items-start gap-2 text-[11px] text-content-subtle">
                        <input type="checkbox" className="mt-0.5" checked={uiRefs} onChange={(e) => setUiRefs(e.target.checked)} />
                        <span>AI mockups around the stub's real frames — each scene restages one library frame on a phone in a marketing scene.</span>
                      </label>
                    )}
                    </>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-2">4. How many</label>
                    {clientFactory && clientShots === 'own' ? (
                      <p className="px-3 py-2 text-xs text-content-subtle">
                        You upload them when you review the package — at least {MIN_CLIENT_SHOTS}.
                      </p>
                    ) : (
                    <select
                      value={shotCount}
                      onChange={(e) => setShotCount(parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface text-content"
                    >
                      {Array.from({ length: MAX_SHOTS }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    )}
                  </div>
                </>
              )}
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="block text-xs font-semibold text-content-muted mb-2">
                  {mode === 'ai' ? '5. Brief (optional)' : 'Brief (optional)'}
                </label>
                <textarea
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={2}
                  placeholder="e.g. bright arcade look, show the board, the combo and the level-up screen"
                  className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm resize-y"
                />
                {mode === 'manual' && (
                  <p className="mt-1 text-[11px] text-content-subtle">Used only if you ask for AI screenshots on the next screen.</p>
                )}
                {mode === 'ai' && !brief.trim() && selectedProject?.brief && (
                  <p className="mt-1 text-[11px] text-content-subtle">Empty — the project's default brief is used: “{selectedProject.brief.slice(0, 160)}{selectedProject.brief.length > 160 ? '…' : ''}”</p>
                )}
                {selectedProject && !selectedProject.logo_key && /(^|[^\p{L}])(logo|лого)/iu.test(`${brief} ${selectedProject.brief ?? ''}`) && (
                  <p className="mt-1 text-[11px] text-warning">
                    The brief asks for the brand logo, but this project has no official logo uploaded — the model will invent one.
                    Upload it via Edit → Official logo.
                  </p>
                )}
              </div>
              {mode === 'ai' && (
                <div className="sm:col-span-2 lg:col-span-4">
                  <label className="block text-xs font-semibold text-content-muted mb-2">Icon prompt (optional)</label>
                  <textarea
                    value={iconPrompt}
                    onChange={(e) => setIconPrompt(e.target.value)}
                    rows={2}
                    placeholder='e.g. elegant letter "T" or a crown, dark blue background with golden trim, luxury minimalist'
                    className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm resize-y"
                  />
                  <p className="mt-1 text-[11px] text-content-subtle">
                    For the icon only: replaces the brief and the stub's game, so the icon is not drawn after the stub. The brand pack and the logo still apply.
                  </p>
                </div>
              )}
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="block text-xs font-semibold text-content-muted mb-2">
                  {mode === 'ai' ? '6. Text meta instructions (optional)' : 'Text meta instructions (optional)'}
                </label>
                <textarea
                  value={metaNotes}
                  onChange={(e) => setMetaNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. mention the daily bonus wheel, keep the tone playful, no sports words"
                  className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm resize-y"
                />
                <div className="mt-2 grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-content-muted mb-1">Keywords the AI must use</label>
                    <input
                      value={runKeywords ?? selectedProject?.keywords ?? ''}
                      onChange={(e) => setRunKeywords(e.target.value)}
                      placeholder="free spins, crash game — comma-separated"
                      className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm"
                    />
                    <p className="mt-1 text-[11px] text-content-subtle">
                      {runKeywords === null ? "The project's keywords; edit to change them for this run only." : 'For this run only — the project keeps its own list.'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-content-muted mb-1">Never translate</label>
                    <input
                      value={protectedTerms}
                      onChange={(e) => setProtectedTerms(e.target.value)}
                      placeholder="Fortune Tiger, Chicken Road"
                      className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm"
                    />
                    <p className="mt-1 text-[11px] text-content-subtle">Kept as written in every locale, on top of the team list in Settings and the brand.</p>
                  </div>
                </div>
              </div>
            </div>
            {projectEditorOpen && (
              <div className="mt-4 p-5 border border-primary/20 bg-primary/5 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-semibold text-content">{editingProjectId ? 'Edit app project' : 'New app project'}</h4>
                    <p className="text-xs text-content-subtle mt-0.5">This prompt defines the product concept, copy direction and visual style.</p>
                  </div>
                  <button type="button" onClick={() => setProjectEditorOpen(false)} className="text-content-subtle hover:text-content">×</button>
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-content-muted mb-1">
                    {editingProjectId ? 'Refill from a brand preset (overwrites the fields below)' : 'Fill from a brand preset'}
                  </label>
                  <Select
                    value={presetName}
                    onChange={applyPreset}
                    searchable
                    placeholder={presets.isLoading ? 'Loading…' : (editingProjectId ? 'Keep as is, or pick a brand' : 'Start from scratch, or pick a brand')}
                    options={(presets.data ?? []).map((item) => ({ value: item.name, label: `${item.name} · ${item.vertical ?? ''}` }))}
                  />
                </div>
                <input value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="Project name, e.g. Tile Rush" className={`w-full ${inputClass}`} />
                <textarea value={projectPrompt} onChange={(event) => setProjectPrompt(event.target.value)} rows={5} placeholder="Creative brief for the listing copy: what you play, the brand's angle, what to avoid…" className={`mt-3 w-full ${inputClass} resize-y`} />
                <div className="mt-3 grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">Brand (exact spelling)</label>
                    <input value={projectPack.brand} onChange={(event) => setProjectPack({ ...projectPack, brand: event.target.value })} placeholder="Defaults to the project name" className={`w-full ${inputClass}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">Vertical</label>
                    <Select
                      value={projectPack.vertical ?? ''}
                      onChange={(value) => setProjectPack({ ...projectPack, vertical: (value || null) as Vertical | null })}
                      options={[{ value: '', label: 'Casino / arcade (default)' }, ...VERTICAL_OPTIONS]}
                    />
                    <p className="mt-1 text-[11px] text-content-subtle">Betting = copy must sell sports betting and casino; anything else stays casino-only.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">Listing meta tone</label>
                    <div className="inline-flex rounded-lg border border-border overflow-hidden">
                      {(['aggressive', 'safe'] as const).map((tone) => (
                        <button
                          key={tone}
                          type="button"
                          onClick={() => setProjectPack({ ...projectPack, meta_tone: tone })}
                          className={`px-3 py-1.5 text-xs ${projectPack.meta_tone === tone ? 'bg-primary text-white font-semibold' : 'text-content-muted hover:bg-surface-muted'}`}
                        >
                          {tone === 'safe' ? 'Safe' : 'Aggressive'}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-[11px] text-content-subtle">Safe = title, short and full never say casino / money (the exact brand name excepted); copy that slips is rewritten.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">
                      Keywords (optional)
                      <button type="button" onClick={() => setMarketOpen(true)} className="ml-2 font-normal text-primary hover:underline" title="Pull keywords, phrases and the winning screenshot story from an ASO Intelligence snapshot">
                        Take from market
                      </button>
                    </label>
                    <input value={projectPack.keywords} onChange={(event) => setProjectPack({ ...projectPack, keywords: event.target.value })} placeholder="free spins, live odds — comma-separated" className={`w-full ${inputClass}`} />
                  </div>
                </div>
                <div className="mt-3 grid sm:grid-cols-[140px_1fr] gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">Brand background</label>
                    <input value={projectPack.brand_bg} onChange={(event) => setProjectPack({ ...projectPack, brand_bg: event.target.value })} placeholder="#FF6600" className={`w-full ${inputClass} font-mono`} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">Brand visual identity (icon + screenshots)</label>
                    <textarea value={projectPack.brand_style} onChange={(event) => setProjectPack({ ...projectPack, brand_style: event.target.value })} rows={3} placeholder="Official logo lockup, colours, UI chrome, what to never draw…" className={`w-full ${inputClass} resize-y`} />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-semibold text-content-muted mb-1">Default artwork brief (optional)</label>
                  <textarea value={projectPack.brief} onChange={(event) => setProjectPack({ ...projectPack, brief: event.target.value })} rows={2} placeholder="Used for the icon and screenshots when a run's brief is left empty" className={`w-full ${inputClass} resize-y`} />
                  {marketOpen && (
                    <MarketPicker
                      onClose={() => setMarketOpen(false)}
                      onPick={(pick) => setProjectPack((p) => ({
                        ...p,
                        keywords: [p.keywords.trim(), pick.keywords].filter(Boolean).join(', '),
                        brief: [p.brief.trim(), pick.brief].filter(Boolean).join('\n\n'),
                      }))}
                    />
                  )}
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs text-content-muted">
                  <input type="checkbox" checked={projectPack.phone_mockups} onChange={(event) => setProjectPack({ ...projectPack, phone_mockups: event.target.checked })} />
                  Store screenshots are phone mockups on a marketing canvas (sportsbook style) instead of full-bleed frames
                </label>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">Screenshot orientation</label>
                    <Select
                      value={projectPack.shot_orient}
                      onChange={(value) => setProjectPack({ ...projectPack, shot_orient: value === 'landscape' ? 'landscape' : 'portrait' })}
                      options={[{ value: 'portrait', label: 'Portrait 9:16 (default)' }, { value: 'landscape', label: 'Landscape 16:9 (e.g. Roulette)' }]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-content-muted mb-1">Official logo (icon reference)</label>
                    <div className="flex items-center gap-3">
                      {projectLogoUrl
                        ? <img src={projectLogoUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-border bg-surface-muted" />
                        : <div className="w-12 h-12 rounded-lg border border-dashed border-border grid place-items-center text-[10px] text-content-subtle">none</div>}
                      <UploadButton label={projectPack.logo_key ? 'Replace' : 'Upload PNG'} disabled={projectSaving} onFiles={uploadProjectLogo} />
                      {projectPack.logo_key && (
                        <button type="button" className="text-[11px] text-content-subtle hover:text-danger" onClick={() => { setProjectPack({ ...projectPack, logo_key: '' }); setProjectLogoUrl(null); }}>Remove</button>
                      )}
                    </div>
                    <p className="mt-1 text-[11px] text-content-subtle">With a logo the icon is generated as a faithful render of it, not from the description.</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div>{editingProjectId && <Button type="button" variant="danger" size="sm" disabled={projectSaving} onClick={removeProject}>Delete</Button>}</div>
                  <div className="flex gap-2"><Button type="button" variant="secondary" size="sm" onClick={() => setProjectEditorOpen(false)}>Cancel</Button><Button type="button" size="sm" loading={projectSaving} onClick={saveProject}>Save project</Button></div>
                </div>
              </div>
            )}
            {error && <p className="mt-4 text-center text-sm text-danger">{error}</p>}
            {conflict && (
              <div className="mt-2 flex justify-center">
                <Button type="button" variant="secondary" size="sm" onClick={openActive}>Open the active job</Button>
              </div>
            )}
            {mode === 'ai' && !conflict && !factoryMode && (
              <div className="mt-2 flex justify-center">
                <button type="button" className="text-xs text-content-subtle hover:text-content underline" onClick={openLast}>
                  Open my last job
                </button>
              </div>
            )}
            <div className="mt-6 flex justify-center">
              {mode === 'ai' ? (
                <Button size="lg"
                  disabled={!projectId || creating || (clientFactory
                    ? !!clientProblem
                    : factoryMode && (!publisherId || !stubId || !!domainProblem || (!!publishOn && (!publishOn.accountId || !publishOn.templateId))))}
                  title={(clientFactory ? clientProblem : domainProblem) ?? undefined} loading={creating} onClick={generate}>
                  <Sparkles className="w-4 h-4 mr-2" />{factoryMode ? 'Start the run' : 'Generate app'}
                </Button>
              ) : (
                <Button size="lg" onClick={startManual}>Fill in manually</Button>
              )}
            </div>
            <p className="mt-3 text-center text-xs text-content-subtle">
              {mode === 'ai'
                ? 'Copy, icon candidates, screenshots and the feature graphic (with AI screenshots) are generated in the background — cards fill in as they finish.'
                : 'Nothing is generated or paid for until you ask for it on a card.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="h-full flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-border">
            <div>
              <p className="text-sm font-semibold text-content">
                {manual ? 'Manual draft' : job ? (
                  <>Job {job.status}{job.subject ? ` · ${job.subject}` : ''}</>
                ) : 'Loading job…'}
              </p>
              <p className="text-xs text-content-subtle">
                {manual
                  ? 'Upload your own assets, or press the AI button on any card you would rather not make by hand.'
                  : 'Edit any text directly or regenerate only the part you do not like.'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {job && !TERMINAL_JOB.includes(job.status) && (
                <Button type="button" variant="secondary" size="sm" disabled={busy === 'cancel'} onClick={cancelJob}>
                  {busy === 'cancel' ? 'Cancelling…' : 'Cancel job'}
                </Button>
              )}
              {!createdAppId && !factoryMode && (
                <>
                  {isTechnical && copy && (
                    <Select size="sm" className="w-52" value={ownerAdminId} options={owners} onChange={onOwnerChange} placeholder="ASO owner" />
                  )}
                  <Button
                    type="button" variant="secondary" size="sm"
                    disabled={!canCreateEarly || !!busy}
                    loading={busy === 'create'}
                    onClick={createEarly}
                    title="Creates the app now with the name, bundle and icon, so keys can be connected and a build started while the rest generates"
                  >
                    Create app now
                  </Button>
                </>
              )}
              {factoryMode ? (
                <>
                  {!createdAppId && isTechnical && copy && (
                    <Select size="sm" className="w-52" value={ownerAdminId} options={owners} onChange={onOwnerChange} placeholder="ASO owner" />
                  )}
                  {!createdAppId && (
                    <Button
                      type="button" variant="secondary" size="sm"
                      disabled={!canCreateEarly || !!busy}
                      loading={busy === 'create'}
                      onClick={createEarly}
                      title={clientFactory
                        ? 'Creates the app now, so its services and domain are set up while you review'
                        : 'Creates the app now, so the Firebase project and the domain are made while you review'}
                    >
                      Create app now
                    </Button>
                  )}
                  <Button
                    type="button"
                    disabled={!copy || usingDraft || !!busy || !factoryRunId || (!createdAppId && !canCreateEarly)
                      || (!!job && !TERMINAL_JOB.includes(job.status)) || tooFewShots}
                    loading={usingDraft}
                    onClick={approveAndBuild}
                    title={job && !TERMINAL_JOB.includes(job.status)
                      ? 'Wait until the run (and any localization) has finished'
                      : tooFewShots
                        ? `Add at least ${MIN_CLIENT_SHOTS} screenshots`
                      : clientFactory
                        ? 'Saves the package as it is on this screen and lets the run build the app'
                        : 'Saves the package as it is on this screen and lets the run build and hand it off'}
                  >
                    Approve &amp; build
                  </Button>
                </>
              ) : createdAppId ? (
                <Button type="button" disabled={!copy || usingDraft} loading={usingDraft} onClick={finishApp}>Finish app</Button>
              ) : (
                <Button type="button" disabled={!draftReady || usingDraft} loading={usingDraft} onClick={useDraft}>Use draft</Button>
              )}
            </div>
          </div>
          {error && <div className="mt-3 p-2.5 rounded-lg bg-danger-soft text-sm text-danger">{error}</div>}
          {factoryMode && (
            <div className="mt-3 p-2.5 rounded-lg bg-surface-muted border border-border text-sm text-content">
              {createdAppId ? (
                <>
                  <span className="font-medium">App created</span>
                  {copy?.bundle_id && <> · <code className="text-xs">{copy.bundle_id}</code></>}
                  <span className="text-content-subtle">
                    {clientFactory
                      ? ' — its services and domain are being set up. Approve & build when the package looks right.'
                      : ' — the Firebase project and the domain are being made. Approve & build when the package looks right.'}
                  </span>
                </>
              ) : (
                <span className="text-content-subtle">
                  {clientFactory
                    ? 'Pick the icon and check the texts and screenshots. "Create app now" starts the app services and the domain early; "Approve & build" does it all.'
                    : 'Pick the icon and check the texts and screenshots. "Create app now" starts Firebase and the domain early; "Approve & build" does it all.'}
                </span>
              )}
            </div>
          )}
          {createdAppId && !factoryMode && (
            <div className="mt-3 p-2.5 rounded-lg bg-surface-muted border border-border text-sm text-content flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="font-medium">App created</span>
                {copy?.bundle_id && <> · <code className="text-xs">{copy.bundle_id}</code></>}
                <span className="text-content-subtle"> — the run keeps going; press Finish app when it is done.</span>
                {isTechnical && (
                  <p className="text-xs text-content-subtle mt-0.5">
                    {buildStarted
                      ? <>Build v{buildStarted.version_name} ({buildStarted.version_code}) dispatched — follow it on the Build tab.</>
                      : readiness?.ok
                        ? 'Ready to build.'
                        : readiness
                          ? <>Not ready to build: {readiness.missing.join(', ')}. Finish these on the Build tab, then come back via "Open my last job". AdMob is optional.</>
                          : 'Checking build readiness…'}
                  </p>
                )}
                {buildError && <p className="text-xs text-danger mt-0.5">{buildError}</p>}
              </div>
              {isTechnical && (
                <div className="flex items-center gap-2 shrink-0">
                  <Button type="button" variant="secondary" size="sm" disabled={!readiness?.ok || !!busy} onClick={() => build('apk')}>Build APK</Button>
                  <Button type="button" variant="secondary" size="sm" disabled={!readiness?.ok || !!busy} onClick={() => build('aab')}>Build AAB</Button>
                </div>
              )}
            </div>
          )}
          {job?.status === 'failed' && <div className="mt-3 p-2.5 rounded-lg bg-danger-soft text-sm text-danger">The job failed — parts that finished are still usable below.</div>}
          {locViews.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs uppercase tracking-wide text-content-subtle">Locale</span>
              {[{ lang: '', label: 'en-US · source' }, ...locViews.map((v) => ({ lang: v.lang, label: v.lang }))].map((item) => (
                <button
                  key={item.lang || 'source'}
                  type="button"
                  onClick={() => setViewLang(item.lang)}
                  className={`rounded-md border px-2.5 py-1 text-xs ${
                    (viewLoc?.lang ?? '') === item.lang
                      ? 'border-primary bg-primary text-white'
                      : 'border-border bg-surface text-content-muted hover:text-content'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
          <div className="grid lg:grid-cols-[minmax(320px,1fr)_390px] gap-8 pt-5 flex-1 min-h-0 overflow-y-auto">
            <div className="space-y-5">
              {viewLoc && <LocalizedListing view={viewLoc} />}
              {/* The source cards stay mounted while a locale is shown, so nothing typed is lost. */}
              <div className={viewLoc ? 'hidden' : 'space-y-5'}>
              {/* Store copy */}
              <section className="border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-content">Store copy</h4>
                  {copy && rerollButton('copy', 'Write with AI', rerollCopy)}
                </div>
                {copy ? (
                  <>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input value={copy.name} onChange={(e) => setCopy({ ...copy, name: e.target.value })} className={inputClass} placeholder="App name" />
                      <div className="flex items-center gap-2">
                        <input value={copy.bundle_id} disabled={!!createdAppId} onChange={(e) => setCopy({ ...copy, bundle_id: e.target.value })} className={`flex-1 min-w-0 ${inputClass} font-mono disabled:opacity-60`} placeholder="Bundle id" title={createdAppId ? 'The app is created — the bundle is frozen' : undefined} />
                        {rerollButton('bundle', 'Suggest', rerollBundle, !!createdAppId)}
                      </div>
                    </div>
                    <input value={copy.short_description} onChange={(e) => setCopy({ ...copy, short_description: e.target.value })} className={`mt-3 w-full ${inputClass}`} placeholder="Short description" />
                    {copy.gnl?.score && (
                      <p className={`mt-2 text-[11px] ${copy.gnl.score.ok ? 'text-success' : 'text-warning'}`}>
                        Google NL: {copy.gnl.score.details.map((d) => `${d.label} ${d.confidence.toFixed(2)}${d.min != null ? ` / ${d.min.toFixed(1)}` : ''} ${d.ok ? '✓' : '✗'}`).join(' · ')}
                        {copy.gnl.attempts > 0 ? ` · ${copy.gnl.attempts} rewrite${copy.gnl.attempts > 1 ? 's' : ''}` : ''}
                      </p>
                    )}
                    <textarea value={copy.full_description} onChange={(e) => setCopy({ ...copy, full_description: e.target.value })} rows={6} className={`mt-3 w-full ${inputClass} resize-y`} placeholder="Full description" />
                  </>
                ) : copyTask?.status === 'failed' ? (
                  <p className="text-sm text-danger">{copyTask.error || 'Copy generation failed.'}</p>
                ) : (
                  <Spinner label="Writing the listing copy…" />
                )}
              </section>

              {/* Icon */}
              <section className="border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-content">Icon</h4>
                  <div className="flex items-center gap-3">
                    <UploadButton label="Upload" multiple disabled={!!busy} onFiles={uploadIcons} />
                    {iconCandidates && rerollButton('icon', 'Draw with AI', () => rerollIcon(false))}
                  </div>
                </div>
                <input
                  value={iconPrompt}
                  onChange={(e) => setIconPrompt(e.target.value)}
                  placeholder={manual ? 'Icon prompt — empty uses the app name' : 'Icon prompt for Draw with AI — empty uses the brief and the app name'}
                  className={`mb-3 w-full ${inputClass}`}
                />
                {iconCandidates && iconCandidates.length > 0 && (
                  <div className="mb-3 flex flex-wrap items-start gap-2">
                    <textarea
                      value={iconFix}
                      onChange={(e) => setIconFix(e.target.value)}
                      rows={1}
                      placeholder="What to change, e.g. bigger logo, no coins, darker background"
                      className={`min-w-0 flex-1 ${inputClass} resize-y`}
                    />
                    <div className="flex flex-col gap-1 pt-1">
                      {rerollButton('icon-fix', 'Fix the picked icon', () => rerollIcon(true), !iconFix.trim() || !chosenIcon)}
                      {rerollButton('icon', 'Redraw with this note', () => rerollIcon(false), !iconFix.trim())}
                    </div>
                  </div>
                )}
                {iconCandidates ? (
                  iconCandidates.length === 0 ? (
                    <p className="text-sm text-content-subtle">
                      {manual ? 'Upload your own icon, or draw candidates with AI.' : 'No candidates came back — regenerate the icon.'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {iconCandidates.map((url) => (
                        <button
                          key={url}
                          type="button"
                          onClick={() => setChosenIcon(url)}
                          className={`border rounded-lg p-1.5 text-left ${chosenIcon === url ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-border-strong'}`}
                        >
                          <img src={url} alt="icon candidate" crossOrigin="anonymous" className="w-full rounded-md aspect-square object-cover" />
                          <span className={`block mt-1.5 text-center text-xs ${chosenIcon === url ? 'text-primary font-medium' : 'text-content-subtle'}`}>
                            {chosenIcon === url ? 'Selected' : 'Select'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )
                ) : iconTask?.status === 'failed' ? (
                  <p className="text-sm text-danger">{iconTask.error || 'Icon generation failed.'}</p>
                ) : (
                  <Spinner label="Drawing icon candidates…" />
                )}
              </section>

              {/* Screenshots */}
              <section className="border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-content">
                    Screenshots{' '}
                    <span className="font-normal text-content-subtle">
                      · {manual || ownShots ? 'yours' : job?.shot_source === 'repo' ? 'real game frames' : 'AI generated'}
                    </span>
                  </h4>
                  <div className="flex items-center gap-3">
                    <UploadButton label="Upload frames" multiple disabled={!!busy || !!composing || !!drawing} onFiles={uploadShots} />
                    {(manual || repoId != null) && (
                      <button
                        type="button"
                        onClick={() => setPresetIndex((i) => (i + 1) % ACCENT_PRESETS.length)}
                        title="Use the next background"
                        className="flex items-center gap-2 px-2 py-1 rounded-md border border-border bg-surface-muted hover:border-border-strong"
                      >
                        <span className="w-4 h-4 rounded border border-border/60 shrink-0" style={{ background: `linear-gradient(to bottom, ${preset.top}, ${preset.bottom})` }} />
                        <span className="text-xs text-content">{(presetIndex % ACCENT_PRESETS.length) + 1} / {ACCENT_PRESETS.length}</span>
                        <span className="text-content-subtle text-xs">Next ›</span>
                      </button>
                    )}
                  </div>
                </div>

                {manual ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-surface-muted/60 border border-border">
                      <span className="text-xs text-content-subtle">Rather have AI draw them?</span>
                      <select
                        value={manualShotCount}
                        disabled={!!drawing}
                        onChange={(e) => setManualShotCount(parseInt(e.target.value, 10))}
                        className="px-2 py-1 text-sm rounded-md border border-border bg-surface text-content disabled:opacity-40"
                      >
                        {Array.from({ length: MAX_SHOTS }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <Button type="button" size="sm" variant="secondary" disabled={!!drawing || !!busy} onClick={drawManualShots}>
                        {drawing ? `Drawing ${drawing.done}/${drawing.total}…` : 'Generate frames'}
                      </Button>
                    </div>

                    {repoId != null && (
                      <RepoFramePicker
                        frames={repoShots.data ?? []}
                        loading={repoShots.isLoading}
                        selected={selectedRepo}
                        captions={repoCaptions}
                        composing={composing}
                        composed={composed}
                        onToggle={toggleRepoFrame}
                        onCaption={(id, value) => setRepoCaptions((m) => ({ ...m, [id]: value }))}
                        onCompose={compose}
                      />
                    )}

                    <AiShotList
                      shots={aiShots}
                      busy={busy}
                      disabled={!!drawing}
                      onCaption={(position, value) => setAiShots((prev) => ({ ...prev, [position]: { ...prev[position], caption: value } }))}
                      onScene={(position, value) => setAiShots((prev) => ({ ...prev, [position]: { ...prev[position], scene: value } }))}
                      onRedo={redoAiShot}
                      onDrop={dropAiShot}
                      rerollButton={rerollButton}
                    />

                    {uploadedShots.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs text-content-subtle">Uploaded frames</p>
                        <UploadedShotGrid shots={uploadedShots} onDrop={(key) => setUploadedShots((prev) => prev.filter((s) => s.key !== key))} />
                      </div>
                    )}

                    {screenshotKeys.length === 0 && !drawing && (
                      <p className="text-sm text-content-subtle">No frames yet — upload your own, pick real ones from the stub, or let AI draw a set.</p>
                    )}
                  </div>
                ) : job?.shot_source === 'repo' ? (
                  <div className="space-y-4">
                    <RepoFramePicker
                      frames={repoShots.data ?? []}
                      loading={repoShots.isLoading}
                      selected={selectedRepo}
                      captions={repoCaptions}
                      composing={composing}
                      composed={composed}
                      onToggle={toggleRepoFrame}
                      onCaption={(id, value) => setRepoCaptions((m) => ({ ...m, [id]: value }))}
                      onCompose={compose}
                    />
                    {uploadedShots.length > 0 && (
                      <UploadedShotGrid shots={uploadedShots} onDrop={(key) => setUploadedShots((prev) => prev.filter((s) => s.key !== key))} />
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {ownShots && uploadedShots.length < MIN_CLIENT_SHOTS && (
                      <p className="text-sm text-content-subtle">
                        Upload your screenshots with Upload frames — at least {MIN_CLIENT_SHOTS}, portrait or landscape, as the store shows them.
                      </p>
                    )}
                    {shotTasks.length === 0 && !ownShots && <Spinner label="Planning screenshots…" />}
                    {shotTasks.filter((t) => !droppedShots.current.has(t.position)).map((t) => {
                      const shot = aiShots[t.position];
                      return (
                        <div key={t.id} className="flex gap-3 items-start border border-border rounded-md p-2">
                          {shot?.url
                            ? <img src={shot.url} alt="" className="h-28 rounded border border-border object-cover shrink-0" />
                            : <div className="h-28 w-16 grid place-items-center rounded border border-border bg-surface-muted shrink-0"><span className="w-3.5 h-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>}
                          <div className="flex-1 space-y-2 min-w-0">
                            {shot ? (
                              <>
                                <input
                                  value={shot.caption}
                                  disabled={busy === `shot-${t.position}`}
                                  onChange={(e) => setAiShots((prev) => ({ ...prev, [t.position]: { ...prev[t.position], caption: e.target.value } }))}
                                  placeholder="Caption — leave empty for an image with no text"
                                  className="w-full px-2 py-1 text-sm rounded-md border border-border bg-surface-muted text-content"
                                />
                                <textarea
                                  value={shot.scene}
                                  rows={2}
                                  disabled={busy === `shot-${t.position}`}
                                  onChange={(e) => setAiShots((prev) => ({ ...prev, [t.position]: { ...prev[t.position], scene: e.target.value } }))}
                                  className="w-full px-2 py-1 text-sm rounded-md border border-border bg-surface-muted text-content"
                                />
                                <div className="flex items-center gap-3">
                                  {rerollButton(`shot-${t.position}`, 'Redo this one', () => redoAiShot(t.position))}
                                  <button
                                    type="button"
                                    disabled={busy === `shot-${t.position}`}
                                    onClick={() => dropAiShot(t.position)}
                                    className="text-xs text-content-muted hover:underline disabled:opacity-40"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </>
                            ) : t.status === 'failed' ? (
                              <p className="text-sm text-danger">{t.error || 'This frame failed.'}</p>
                            ) : (
                              <Spinner label={`Frame ${t.position + 1}: drawing…`} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {uploadedShots.length > 0 && (
                      <UploadedShotGrid shots={uploadedShots} onDrop={(key) => setUploadedShots((prev) => prev.filter((s) => s.key !== key))} />
                    )}
                  </div>
                )}
              </section>

              {/* Feature banner */}
              <BannerCard
                sourceUrl={bannerSource}
                preset={preset}
                headline={headline}
                onHeadline={setHeadline}
                filenameBase={copy?.name || 'app'}
                saved={feature}
                onSaved={setFeature}
                onError={setError}
                onSaver={(fn) => { bannerSaver.current = fn; }}
                ai={{
                  status: bannerTask?.status ?? null,
                  error: bannerTask?.status === 'failed' ? bannerTask.error : null,
                  tagline: aiBanner.tagline,
                  onTagline: (tagline) => setAiBanner((prev) => ({ ...prev, tagline })),
                  drawButton: rerollButton('banner', feature ? 'Redraw with AI' : 'Draw with AI', drawAiBanner, !copy?.name.trim()),
                }}
              />

              {/* Localizations */}
              {!manual && (
                <section className="border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-content">Localizations</h4>
                    {locStarted && <span className="text-xs text-content-subtle">{locProgress.filter((p) => p.complete).length} / {locProgress.length} locales ready</span>}
                  </div>
                  {!locStarted ? (
                    !copy || (job && !TERMINAL_JOB.includes(job.status)) ? (
                      <p className="text-sm text-content-subtle">Available once generation finishes — review the English draft first.</p>
                    ) : job?.status !== 'done' ? (
                      <p className="text-sm text-content-subtle">The generation run did not finish cleanly — localization needs a done job.</p>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-content-subtle">
                          Texts are transcreated within Play limits; captions on the current screenshots are translated and re-rendered.
                          The result is saved with the app as a listing package when you use the draft.
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-56 overflow-y-auto pr-1">
                          {PLAY_LOCALES.filter((l) => l.code !== 'en-US').map((l) => (
                            <label key={l.code} className="flex items-center gap-1.5 text-sm text-content">
                              <input
                                type="checkbox"
                                checked={locLangs.includes(l.code)}
                                onChange={(e) => setLocLangs((prev) => e.target.checked ? [...prev, l.code] : prev.filter((x) => x !== l.code))}
                              />
                              <span className="truncate" title={`${l.name} · ${l.code}`}>{l.name}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex items-center gap-3">
                          <Button type="button" size="sm" disabled={!canLocalize || locLangs.length === 0 || locBusy} loading={locBusy} onClick={startLocalization}>
                            Localize {locLangs.length > 0 ? `${locLangs.length} locale(s)` : ''}
                          </Button>
                          {locLangs.length > 0 && (
                            <button type="button" className="text-xs text-content-muted hover:underline" onClick={() => setLocLangs([])}>Clear</button>
                          )}
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="space-y-1.5">
                      {locProgress.map((p) => (
                        <div key={p.lang} className="flex items-center gap-2 text-sm">
                          <span className="w-16 shrink-0 font-mono text-xs text-content-muted">{p.lang}</span>
                          {p.complete ? (
                            <span className="text-green-600 text-xs">✓ ready</span>
                          ) : p.text === 'failed' ? (
                            <span className="text-danger text-xs">text failed</span>
                          ) : (
                            <span className="text-content-subtle text-xs inline-flex items-center gap-1.5">
                              {p.text === 'done' ? 'text ✓' : <>text <span className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" /></>}
                              {p.shotsTotal > 0 && <> · shots {p.shotsDone}/{p.shotsTotal}{p.shotsFailed > 0 ? ` (${p.shotsFailed} failed)` : ''}</>}
                            </span>
                          )}
                          {p.fits === false && <span className="text-warning text-xs" title="The localized text overflows Play limits — trim it in the drafts editor later.">over Play limits</span>}
                        </div>
                      ))}
                      <p className="pt-1 text-xs text-content-subtle">Localizations are saved with the app as a listing package when you use the draft.</p>
                    </div>
                  )}
                  {locError && <p className="mt-2 text-sm text-danger">{locError}</p>}
                </section>
              )}
              </div>
            </div>
            <PlayPreview
              iconUrl={chosenIcon || iconCandidates?.[0] || ''}
              name={viewLoc ? viewLoc.title : (copy?.name || '…')}
              shortDescription={viewLoc ? viewLoc.short_description : (copy?.short_description || '')}
              fullDescription={viewLoc ? viewLoc.full_description : (copy?.full_description || '')}
              screenshots={viewLoc ? viewLoc.shots.map((s) => s.url) : previewShotUrls}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

/** The stub's real frame library: pick frames, caption them, compose to uploads. */
function RepoFramePicker({ frames, loading, selected, captions, composing, composed, onToggle, onCaption, onCompose }: {
  frames: StubScreenshot[];
  loading: boolean;
  selected: number[];
  captions: Record<number, string>;
  composing: { done: number; total: number } | null;
  composed: { id: number; key: string; url: string }[];
  onToggle: (id: number) => void;
  onCaption: (id: number, value: string) => void;
  onCompose: () => void;
}) {
  if (loading) return <Spinner label="Loading the stub's frame library…" />;
  if (frames.length === 0) return <p className="text-sm text-content-subtle">This stub has no frames in its library.</p>;
  return (
    <div className="space-y-3">
      <p className="text-xs text-content-subtle">
        Pick the frames to ship — captions are prefilled from the plan in the order you select, and every frame is
        composed on the background chosen above.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {frames.map((frame) => {
          const order = selected.indexOf(frame.id);
          const isSelected = order >= 0;
          return (
            <div key={frame.id} className={`border rounded-lg p-2 ${isSelected ? 'border-primary' : 'border-border'}`}>
              <button type="button" onClick={() => onToggle(frame.id)} className="relative block w-full">
                <img src={frame.url} alt="" className="w-full aspect-[9/16] object-cover rounded-md bg-surface-muted" />
                <span className={`absolute top-1.5 left-1.5 w-5 h-5 rounded-full text-[11px] font-semibold grid place-items-center ${isSelected ? 'bg-primary text-white' : 'bg-surface/80 border border-border text-content-subtle'}`}>
                  {isSelected ? order + 1 : ''}
                </span>
              </button>
              {isSelected && (
                <input
                  value={captions[frame.id] ?? ''}
                  disabled={!!composing}
                  onChange={(e) => onCaption(frame.id, e.target.value)}
                  placeholder="Caption — empty for no text"
                  className="mt-2 w-full px-2 py-1 text-xs rounded-md border border-border bg-surface-muted text-content"
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3">
        <Button type="button" size="sm" disabled={selected.length === 0 || !!composing} onClick={onCompose}>
          {composing ? `Composing ${composing.done}/${composing.total}…` : `Compose ${selected.length} screenshot(s)`}
        </Button>
        {composed.length > 0 && !composing && <span className="text-xs text-content-subtle">{composed.length} frame(s) uploaded</span>}
      </div>
      {composed.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {composed.map((c) => (
            <img key={c.key} src={c.url} alt="" className="w-full aspect-[9/16] object-contain bg-surface-muted rounded-lg border border-border" />
          ))}
        </div>
      )}
    </div>
  );
}

/** AI frames drawn without a job behind them — editable and individually redoable. */
function AiShotList({ shots, busy, disabled, onCaption, onScene, onRedo, onDrop, rerollButton }: {
  shots: Record<number, WizardScreenshotResult>;
  busy: string | null;
  disabled: boolean;
  onCaption: (position: number, value: string) => void;
  onScene: (position: number, value: string) => void;
  onRedo: (position: number) => void;
  onDrop: (position: number) => void;
  rerollButton: (key: string, label: string, onClick: () => void, disabled?: boolean) => JSX.Element;
}) {
  const positions = Object.keys(shots).map(Number).sort((a, b) => a - b);
  if (positions.length === 0) return null;
  return (
    <div className="space-y-2">
      {positions.map((position) => {
        const shot = shots[position];
        return (
          <div key={position} className="flex gap-3 items-start border border-border rounded-md p-2">
            <img src={shot.url} alt="" className="h-28 rounded border border-border object-cover shrink-0" />
            <div className="flex-1 space-y-2 min-w-0">
              <input
                value={shot.caption}
                disabled={disabled || busy === `shot-${position}`}
                onChange={(e) => onCaption(position, e.target.value)}
                placeholder="Caption — leave empty for an image with no text"
                className="w-full px-2 py-1 text-sm rounded-md border border-border bg-surface-muted text-content"
              />
              <textarea
                value={shot.scene}
                rows={2}
                disabled={disabled || busy === `shot-${position}`}
                onChange={(e) => onScene(position, e.target.value)}
                className="w-full px-2 py-1 text-sm rounded-md border border-border bg-surface-muted text-content"
              />
              <div className="flex items-center gap-3">
                {rerollButton(`shot-${position}`, 'Redo this one', () => onRedo(position), disabled)}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onDrop(position)}
                  className="text-xs text-content-muted hover:underline disabled:opacity-40"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UploadedShotGrid({ shots, onDrop }: {
  shots: { key: string; url: string }[];
  onDrop: (key: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {shots.map((s) => (
        <div key={s.key} className="relative">
          <img src={s.url} alt="" className="w-full aspect-[9/16] object-contain bg-surface-muted rounded-lg border border-border" />
          <button
            type="button"
            onClick={() => onDrop(s.key)}
            title="Remove this frame"
            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-surface/90 border border-border text-content-subtle hover:text-danger"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function BannerCard({ sourceUrl, preset, headline, onHeadline, filenameBase, saved, onSaved, onError, onSaver, ai }: {
  sourceUrl: string | undefined;
  preset: AccentPreset;
  headline: string;
  onHeadline: (v: string) => void;
  filenameBase: string;
  /** The banner that will be saved with the app, once there is one. */
  saved: { key: string; url: string } | null;
  onSaved: (value: { key: string; url: string } | null) => void;
  onError: (message: string) => void;
  /** Hands the parent a way to save the rendered banner, so Use draft can do it unasked. */
  onSaver?: (fn: (() => Promise<{ key: string; url: string }>) | null) => void;
  /** The AI banner: the run's background task and the Draw with AI button. */
  ai?: {
    status: string | null;
    error: string | null;
    /** undefined until the AI wrote one or the operator typed one. */
    tagline: string | undefined;
    onTagline: (value: string) => void;
    drawButton: JSX.Element;
  };
}) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  // The saved banner came from the renderer below (not a hand upload). Only
  // then does the renderer stay open for a new headline: an uploaded banner is
  // the banner, and a second one drawn from the screenshot is just noise.
  const [renderedKey, setRenderedKey] = useState('');
  const uploadedByHand = !!saved && saved.key !== renderedKey;

  useEffect(() => {
    let alive = true;
    setImg(null); setLoadError('');
    if (!sourceUrl || uploadedByHand) return;
    loadImage(sourceUrl)
      .then((i) => { if (alive) setImg(i); })
      .catch(() => { if (alive) setLoadError('Could not load the screenshot for the banner.'); });
    return () => { alive = false; };
  }, [sourceUrl, uploadedByHand]);

  const canvas = useMemo(
    () => (img && !uploadedByHand ? renderBanner(img, { preset, caption: headline }) : null),
    [img, preset, headline, uploadedByHand],
  );
  const dataUrl = useMemo(() => canvas?.toDataURL('image/png') ?? '', [canvas]);

  const upload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      onSaved(await uploadCreateAsset(await prepareImageUpload(file, WIZARD_FEATURE_BANNER), 'feature'));
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Banner upload failed');
    } finally {
      setSaving(false);
    }
  };

  const saveRendered = async () => {
    if (!canvas) return;
    setSaving(true);
    try {
      const out = await uploadCreateAsset(
        await prepareImageUpload(await canvasToBlob(canvas), WIZARD_FEATURE_BANNER), 'feature',
      );
      setRenderedKey(out.key);
      onSaved(out);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Could not save the banner');
    } finally {
      setSaving(false);
    }
  };

  // Use draft saves the rendered banner itself when the operator did not:
  // an app without a feature graphic cannot be published, and the preview
  // looked saved.
  useEffect(() => {
    if (!onSaver) return;
    onSaver(canvas ? async () => {
      const out = await uploadCreateAsset(
        await prepareImageUpload(await canvasToBlob(canvas), WIZARD_FEATURE_BANNER), 'feature',
      );
      setRenderedKey(out.key);
      onSaved(out);
      return out;
    } : null);
    return () => onSaver(null);
  }, [canvas, onSaver, onSaved]);

  return (
    <section className="border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-content">Feature banner</h4>
        <div className="flex items-center gap-3">
          {ai?.drawButton}
          <UploadButton label={uploadedByHand ? 'Replace' : 'Upload 1024×500'} disabled={saving} onFiles={upload} />
          {!uploadedByHand && <span className="text-xs text-content-subtle">or render it from the first screenshot</span>}
        </div>
      </div>

      {ai && (ai.status === 'pending' || ai.status === 'running') && !saved && (
        <div className="mb-3"><Spinner label="Drawing the feature graphic in the screenshots' style…" /></div>
      )}
      {ai?.error && !saved && <p className="mb-3 text-sm text-danger">AI banner failed: {ai.error}</p>}
      {ai && ai.tagline !== undefined && (
        <div className="mb-3">
          <label className="block mb-1 text-[11px] font-semibold uppercase tracking-wide text-content-subtle">
            AI banner tagline — used by the next redraw; empty = the app name alone
          </label>
          <input
            value={ai.tagline}
            onChange={(e) => ai.onTagline(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm"
          />
        </div>
      )}

      {saved && (
        <div className="mb-3 space-y-2">
          <img src={saved.url} alt="feature banner" className="w-full rounded-md border border-border/50" />
          <div className="flex items-center gap-3 text-xs">
            <span className="text-green-600">✓ saved with the app when you use the draft</span>
            <button type="button" onClick={() => onSaved(null)} className="text-content-muted hover:underline">
              {uploadedByHand && sourceUrl ? 'Remove and render from the first screenshot' : 'Remove'}
            </button>
          </div>
        </div>
      )}

      {uploadedByHand ? null : !sourceUrl ? (
        !saved && <p className="text-sm text-content-subtle">Upload your own banner, or add a screenshot first to render one.</p>
      ) : loadError ? (
        <p className="text-sm text-danger">{loadError}</p>
      ) : !canvas ? (
        <Spinner label="Rendering the banner…" />
      ) : (
        <div className="space-y-3">
          <input
            placeholder="Banner headline (optional)"
            value={headline}
            onChange={(e) => onHeadline(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm"
          />
          <img src={dataUrl} alt="banner preview" className="w-full rounded-md border border-border/50" />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-content-subtle text-xs">{canvas.width}×{canvas.height}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => downloadCanvas(canvas, `${safeSlug(headline || filenameBase)}-banner.png`)}
                className="px-2 py-1 text-xs rounded-md border border-border text-content-muted hover:bg-surface-muted"
              >
                Download PNG
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={saveRendered}
                className="px-2 py-1 text-xs rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
              >
                {saving ? 'Saving…' : saved ? 'Replace saved banner' : 'Save with the app'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/** A finished localization, read-only: its texts and its re-rendered frames. */
function LocalizedListing({ view }: { view: LocalizedView }) {
  const field = (label: string, value: string, max: number) => (
    <div>
      <div className="mb-1 flex items-center justify-between text-[11px] text-content-subtle">
        <span className="font-semibold uppercase tracking-wide">{label}</span>
        <span className={value.length > max ? 'text-warning' : ''}>{value.length} / {max}</span>
      </div>
      <p className="whitespace-pre-wrap rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-content">{value || '—'}</p>
    </div>
  );
  return (
    <section className="border border-border rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-content">Localized listing · {view.lang}</h4>
        <span className="text-[11px] text-content-subtle">Read-only here — edit after creation on the Share tab</span>
      </div>
      {view.missing_terms && view.missing_terms.length > 0 && (
        <p className="text-xs text-warning">Translated anyway: {view.missing_terms.join(', ')} — fix by hand before publishing.</p>
      )}
      {field('Title', view.title, 30)}
      {field('Short description', view.short_description, 80)}
      {field('Full description', view.full_description, 4000)}
      <div>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-content-subtle">Screenshots ({view.shots.length})</p>
        {view.shots.length === 0 ? (
          <p className="text-sm text-content-subtle">No localized frames for this locale.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {view.shots.map((s) => (
              <a key={s.url} href={s.url} target="_blank" rel="noreferrer" title={s.caption}>
                <img src={s.url} alt={s.caption} className="w-full rounded-md border border-border object-cover" />
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function PlayPreview({ iconUrl, name, shortDescription, fullDescription, screenshots }: {
  iconUrl: string; name: string; shortDescription: string; fullDescription: string; screenshots: string[];
}) {
  return (
    <div className="lg:sticky lg:top-0 self-start mx-auto w-full max-w-[390px]">
      <p className="mb-2 text-center text-xs font-medium text-content-subtle">LIVE PREVIEW · GOOGLE PLAY</p>
      <div className="rounded-[2.25rem] border-[7px] border-content bg-surface shadow-xl overflow-hidden">
        <div className="h-7 px-5 flex items-center justify-between text-[10px] font-semibold"><span>9:41</span><span>● ◔ ▰</span></div>
        <div className="px-4 pb-6 max-h-[690px] overflow-y-auto">
          <div className="flex gap-3 pt-3">
            <div className="w-20 h-20 shrink-0 overflow-hidden bg-surface-muted rounded-2xl">
              {iconUrl && <img src={iconUrl} className="w-full h-full object-cover" alt="" />}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-content leading-tight truncate">{name}</h3>
              <p className="text-xs text-primary mt-1 truncate">Developer</p>
              <p className="text-xs text-content-subtle mt-1">Contains ads · In-app purchases</p>
              <button type="button" className="mt-2 px-5 py-1.5 text-xs font-semibold text-white bg-primary rounded-md">Install</button>
            </div>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border my-5 text-center">
            <PreviewMetric value="4.7" label="★ rating" />
            <PreviewMetric value="100K+" label="downloads" />
            <PreviewMetric value="18 MB" label="size" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {screenshots.map((url, index) => (
              <img key={`${url}-${index}`} src={url} alt="" className="w-[42%] shrink-0 aspect-[9/16] object-contain bg-surface-muted rounded-lg border border-border" />
            ))}
            {screenshots.length === 0 && (
              <div className="w-[42%] shrink-0 aspect-[9/16] grid place-items-center bg-surface-muted rounded-lg border border-border text-xs text-content-subtle">No frames yet</div>
            )}
          </div>
          <h4 className="font-semibold text-content mt-5">About this app</h4>
          <p className="mt-2 text-sm text-content-muted whitespace-pre-line line-clamp-6">{fullDescription || shortDescription}</p>
        </div>
      </div>
    </div>
  );
}

function PreviewMetric({ value, label }: { value: string; label: string }) {
  return <div className="px-1"><p className="text-sm font-semibold text-content">{value}</p><p className="text-[10px] text-content-subtle">{label}</p></div>;
}
